<template>
    <div class="my-6">
        <div>
            <div class="flex justify-start w-full pb-1">
                <h3 class="text-xl font-medium leading-5 text-gray-900">
                    Facilities
                </h3>
            </div>
            <div v-if="currentFacilities.length > 0" class="block gap-4 mt-4 md:grid md:grid-cols-4 lg:grid-cols-5">
                <div
                    v-for="(facility) in currentFacilities"
                    :key="facility.id"
                    class="flex-wrap text-left bg-white border border-gray-300 rounded-lg shadow md:col-span-1 md:text-center md:grid md:grid-rows-5 md:max-h-72"
                >
                    <button @click="openDeleteModal(facility)" class="self-start block row-span-1 mt-2 mr-2 text-red-600 rounded-full md:max-h-4 justify-self-end md:bg-white md:rounded-none">
                        <span class="inline-flex text-xs md:sm">
                            Remove
                        </span>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="inline-flex w-4 h-4 text-red-600 md:h-5 md:w-5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                        </svg>
                    </button>
                    <a
                    :href="getRoute(facility)"
                    class="block hover:bg-gray-50">
                        <div class="flex my-2 md:flex-col md:flex-1 md:row-span-4 md:my-0">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-10 h-10 ml-2 text-gray-600 bg-white shadow md:h-20 md:rounded-2xl md:w-20 rounded-xl md:ml-0" viewBox="3 3 18 18" preserveAspectRatio="xMinYMid meet" fill="currentColor">
                                <path d="M0 0h24v24H0V0z" fill="none" />
                                <path
                                    d="M19 3H5c-1.1 0-1.99.9-1.99 2L3 19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-8.5-2h3v-3.5H17v-3h-3.5V7h-3v3.5H7v3h3.5z" />
                            </svg>
                            <h3 class="ml-2 font-medium text-gray-600 md:ml-0 md:mt-3 text-md md:text-sm">{{ facility.name }}</h3>
                            <dl class="flex-col flex-grow hidden mt-1 md:flex">
                                <dd class="text-sm text-gray-500">{{ facility.city }}, {{ facility.state }}</dd>
                                <dd class="mt-1">
                                    <span class="px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">{{ facility.firm_type }}</span>
                                </dd>
                            </dl>
                        </div>
                        <div class="flex mb-2 md:hidden">
                            <dd class="inline-block ml-2 text-sm text-gray-500">{{ facility.city }}, {{ facility.state }}</dd>
                            <dd class="inline-block ml-2">
                                <span class="px-2 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-full">{{ facility.firm_type }}</span>
                            </dd>
                        </div>
                    </a>
                </div>
                <div class="flex items-center flex-shrink-0 col-span-1 mt-2 justify-self-center md:justify-self-start md:mt-0">
                    <button v-if="currentFacilities.length > 0" @click="openAddFacilityModal()" class="w-10 h-10 text-center bg-green-600 rounded-full md:w-12 md:h-12 hover:bg-green-700 justify-items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-10 h-10 text-center text-white">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                        </svg>
                    </button>
                </div>
            </div>
            <div v-else class="mt-4">
                <div class="p-6 text-center border-2 border-gray-200 border-dashed rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-12 h-12 mx-auto text-gray-400">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z" />
                    </svg>
                    <h3 class="mt-2 text-lg font-medium text-gray-900">You haven't added any facilities for this profile yet!</h3>
                    <p class="mt-1 text-gray-500 text-md">These connections are for your eyes only! Keep that rolodex up to date!</p>
                    <p class="mt-1 text-gray-500 text-md">Click below to link facilities to this profile.</p>
                    <div class="mt-6">
                        <button class="tw-button-green" @click="openAddFacilityModal()">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-5 h-5 mr-1 text-white">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                            </svg>
                            Add Facility
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <profile-suggested-facilities
            @add-to-surgeon = "addSuggestedFacility"
            :search-filters="searchFilters"
            :search-route="suggestedSearchRoute"
            :model="model"
            :facilities="currentFacilities"
        >
        </profile-suggested-facilities>
        <tw-modal ref="add_facility">
            <template v-slot:content>
                <div class="w-full px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
                    <div class="w-full sm:flex sm:items-start">
                        <div class="flex items-center justify-center flex-shrink-0 p-2 mx-auto bg-gray-100 rounded-full w-14 h-14 sm:mx-0 sm:h-10 sm:w-10">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-10 h-10 mx-auto text-gray-500">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Zm0 3h.008v.008h-.008v-.008Z" />
                            </svg>
                        </div>
                        <div class="w-full mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                            <h3 class="text-lg leading-6 text-gray-600">
                                Add a Facility
                            </h3>
                        </div>
                    </div>
                    <div class="w-full mt-3">
                        <div v-if="selectedFacilities.length > 0" class="my-1">
                            <span
                            v-for="(facility, index) in selectedFacilities"
                            class="inline-flex items-center py-0.5 pl-2 pr-2 rounded-full text-xxs font-medium bg-blue-100 text-blue-800 truncate">
                                {{ facility.name }}
                                <button
                                type="button"
                                class="flex-shrink-0 ml-0.5 h-4 w-4 rounded-full inline-flex items-center justify-center text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none focus:bg-blue-500 focus:text-white"
                                @click="removeFacility(index, value)"
                            >
                            <svg class="w-2 h-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                            <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7" />
                            </svg>
                            </button>
                            </span>

                        </div>
                        <input
                            v-debounce:400ms="updateSearch"
                            type="search"
                            id="elastic-search-input"
                            @search="searchClear"
                            placeholder="Search Facilities"
                            class="block w-full p-2 pl-8 text-xs border-gray-300 rounded-md shadow focus:border-blue-500 focus:ring-0 md:p-4 md:pl-12 sm:p-3 sm:leading-5 sm:text-sm"
                        />
                        <ul v-if="searchResults.length > 0" class="mt-2 overflow-y-scroll border-gray-200 rounded-lg border-1 max-h-44">
                            <li v-for="(facility, index) in searchResults">
                                <a @click="selectFacility(facility)" class="block transition duration-150 ease-in-out hover:bg-gray-50 focus:outline-none focus:bg-gray-50">
                                    <div class="flex items-center px-4 py-4 sm:px-6">
                                    <div class="flex items-center flex-1 min-w-0">
                                        <div
                                        class="flex-1 min-w-0 md:px-4 md:grid md:grid-cols-4 md:gap-4"
                                        >
                                        <div class="col-span-4 space-y-1">
                                            <div
                                            class="flex space-x-2 text-sm font-semibold leading-5 text-blue-900 truncate hover:text-blue-700 visited:text-indigo-800"
                                            >
                                            <span
                                                class="flex-shrink-0 truncate lg:flex-shrink"
                                                v-html="facility.name_edge_ngram"
                                            >
                                                {{ facility.name }}
                                            </span>
                                            </div>
                                            <div
                                            v-if="facility.city_state_edge_ngram"
                                            class="flex items-center text-sm leading-5 text-gray-500 sm:mt-0"
                                            >
                                            <svg
                                                class="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path
                                                fill-rule="evenodd"
                                                d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                                clip-rule="evenodd"
                                                />
                                            </svg>
                                            <span v-html="facility.city_state_edge_ngram"></span>
                                            </div>
                                        </div>
                                    </div>
                                    </div>

                                    </div>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="flex justify-end px-4 py-3 space-x-2 sm:space-x-4 bg-gray-50 sm:px-6">
                    <button class="tw-button-gray" @click="closeModal">
                        Cancel
                    </button>
                    <button class="tw-button-green" @click="addFacility()">
                        Add
                    </button>
            </div>
            </template>
        </tw-modal>
        <tw-modal ref="delete_facility_modal">
            <template v-slot:content>
            <div class="w-full px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
                    <div class="w-full sm:flex sm:items-start">
                        <div class="flex items-center justify-center flex-shrink-0 p-2 mx-auto bg-red-100 rounded-full w-14 h-14 sm:mx-0 sm:h-10 sm:w-10">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-red-600">
                                <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                            </svg>
                        </div>
                        <div class="w-full mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                            <h3 class="text-lg leading-6 text-gray-600">
                                Are you sure you want to remove this facility?
                            </h3>
                        </div>
                    </div>
                </div>
                <div class="flex justify-end px-4 py-3 space-x-2 sm:space-x-4 bg-gray-50 sm:px-6">
                    <button class="tw-button-gray" @click="closeDeleteModal">
                        Cancel
                    </button>
                    <button class="tw-button-red" @click="deleteFacility(removedFacility)">
                        Remove
                    </button>
                </div>
            </template>
        </tw-modal>
    </div>
</template>
<script>
import { getDirective } from 'vue-debounce'
export default {
    directives: {
        debounce: getDirective()
    },
    props: {
        facilities: {
            type: [Array, Object],
            required: false,
        },
        submitRoute: {
            type: String,
            required: true,
        },
        removeRoute: {
            type: String,
            required: true,
        },
        searchFilters: {
            type: [Array, Object],
            required: true,
        },
        suggestedSearchRoute: {
            type: String,
            required: true,
        },
        model: {
            type: [Array, Object],
            required: true,
        }
    },
    data() {
        return {
            isLoading: false,
            selectedFacilities: [],
            currentFacilities: this.facilities,
            searchRoute: this.route('search.filtered_results', 'facilities'),
            serverParams: {
                searchTerm: '',
                filters: {
                    'type': ['asc', 'hospital']
                },
                pageSize: 100,
                page: 0,
            },
            searchResults: {},
            removedFacility: {},
        }
    },
    methods: {
        getRoute(facility) {
            switch(facility.type){
                case 'hospital':
                    return this.route('hospitals.show', { hospital: facility.id })
                case 'asc':
                    return this.route('ascs.show', { asc : facility.id })
                default:
                    '#'
            }
        },
        openAddFacilityModal() {
            this.$refs.add_facility.show();
        },
        async loadItems() {
            this.isLoading = true;
            try {
                const response = await axios.get(this.searchRoute, { params: this.serverParams });
                this.searchResults = response.data.results;
                this.isLoading = false;
            } catch (error) {
                this.rows = [];
                this.isLoading = false;
                return false;
            }

            return true;
        },
        updateSearch(val, e) {
            this.serverParams.page = 0
            this.serverParams.searchTerm = val
            this.loadItems();
        },
        searchClear() {
            if (document.getElementById('elastic-search-input').value === '') {
                this.serverParams.searchTerm = document.getElementById('elastic-search-input').value;
                this.searchResults = {};
            }
        },
        selectFacility(facility) {
            facility.name = facility.name.replace(/<\/?[^>]+(>|$)/g, "");
            this.selectedFacilities.push(facility);
        },
        closeModal() {
            this.$refs.add_facility.hide();
            this.searchResults = {};
            this.selectedFacilities = [];
        },
        removeFacility(index) {
            this.selectedFacilities.splice(index, 1);
        },
        async addFacility() {
            const ids = this.selectedFacilities.map(facility => facility.id)
            const result = await axios.get(this.submitRoute, { 'params': ids })
            this.currentFacilities = result.data;
            this.searchResult = {}
            this.selectedFacilities = []
            this.closeModal()
        },
        async deleteFacility(facility) {
            const result = await axios.get(this.removeRoute, { 'params': {'facility': facility.id} })
            this.currentFacilities = result.data;
            this.closeDeleteModal()
            this.removedFacility = {}
        },
        openDeleteModal(facility) {
            this.$refs.delete_facility_modal.show()
            this.removedFacility = facility
        },
        closeDeleteModal() {
            this.$refs.delete_facility_modal.hide()
            this.removedFacility = {}
        },
        async addSuggestedFacility(facility_id) {
            const result = await axios.get(this.submitRoute, {'params': [facility_id]})
            this.currentFacilities = result.data;
        }
    }
}
</script>
